import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Slot, Slottable } from '@radix-ui/react-slot'
import * as React from 'react'

import { Button } from '../actions/button'
import { cn } from '../lib/cn'
import { IconClose, IconMenu } from '../lib/icons'
import { overlayVeil } from '../lib/surfaces'
import { AppShellNavLink, type AppShellNavLinkProps } from './app-shell-nav'
import { closeOnFollowedLink } from './shell-region'

// The five slots and which ones a count fills, read left to right: home is always the
// third, so the thumb finds it in the same place whatever the role.
const SLOTS_BY_COUNT: Record<number, readonly number[]> = {
  0: [],
  1: [4],
  2: [2, 4],
  3: [2, 4, 5],
  4: [1, 2, 4, 5],
}
const MAX_WITHOUT_MORE = 4
const VISIBLE_WITH_MORE = 3
const SLOTS_WITH_MORE: readonly number[] = [1, 2, 4]
const MORE_SLOT = 5
const HOME_SLOT = 3
const COLUMN_START = ['col-start-1', 'col-start-2', 'col-start-3', 'col-start-4', 'col-start-5']

/** How a link draws itself: the same `AppShellBarLink` sits in the pill, in its centre disc
 *  and in the More panel, and each place asks for a different shape. */
type BarSlot = 'item' | 'home' | 'sheet'
const BarSlotContext = React.createContext<BarSlot>('item')

// Icon above the words. `min-h-11` is the thumb target; the words truncate because five of
// them share 375px, and a navigation label is not an identity.
const barItem =
  'flex min-h-11 w-full min-w-0 flex-col items-center justify-center gap-0.5 rounded-pill px-1 text-tab text-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:focus-volee'
const barItemCurrent = 'text-primary-text hover:text-primary-text'
const barHome =
  'flex size-(--size-bar-home) items-center justify-center rounded-pill bg-primary text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:focus-volee'

export type AppShellBarLinkProps = AppShellNavLinkProps

/** A destination in `AppShellBottomBar`. Takes the props of `AppShellNavLink`, so an app fills
 *  the bar and the column with the same entries. */
export const AppShellBarLink = React.forwardRef<HTMLAnchorElement, AppShellBarLinkProps>(
  (props, ref) => {
    const slot = React.useContext(BarSlotContext)
    if (slot === 'sheet') return <AppShellNavLink ref={ref} {...props} />

    const {
      className,
      label,
      active = false,
      asChild = false,
      icon: Icon,
      children,
      ...rest
    } = props
    const Component = asChild ? Slot : 'a'
    const home = slot === 'home'
    return (
      <Component
        ref={ref}
        aria-current={active ? 'page' : undefined}
        className={cn(home ? barHome : barItem, !home && active && barItemCurrent, className)}
        {...rest}
      >
        <Slottable>{children}</Slottable>
        {Icon === undefined ? null : (
          <Icon aria-hidden="true" className={home ? 'size-6' : 'size-5 shrink-0'} />
        )}
        {home ? (
          <span className="sr-only">{label}</span>
        ) : (
          <span className="max-w-full truncate">{label}</span>
        )}
      </Component>
    )
  },
)
AppShellBarLink.displayName = 'AppShellBarLink'

export interface AppShellBottomBarProps {
  /** The centre: the role's home, an `AppShellBarLink` drawn in the lime disc. */
  home: React.ReactNode
  /** The other destinations, as `AppShellBarLink`, in priority order. Pass an array or direct
   *  children, not a Fragment: a Fragment counts as one entry. */
  children?: React.ReactNode
  /** aria-label of the bar. */
  label: string
  /** The fifth slot's words when there are more than four destinations, and its panel's title. */
  moreLabel: string
  /** aria-label of the button that closes that panel. */
  closeLabel: string
}

export function AppShellBottomBar({
  home,
  children,
  label,
  moreLabel,
  closeLabel,
}: AppShellBottomBarProps) {
  const [moreOpen, setMoreOpen] = React.useState(false)
  const destinations = React.Children.toArray(children)
  const needsMore = destinations.length > MAX_WITHOUT_MORE
  const shown = needsMore ? destinations.slice(0, VISIBLE_WITH_MORE) : destinations
  const overflow = needsMore ? destinations.slice(VISIBLE_WITH_MORE) : []
  const places = needsMore ? SLOTS_WITH_MORE : (SLOTS_BY_COUNT[shown.length] ?? [])

  function moreButton() {
    return (
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          className={cn(barItem, 'data-[state=open]:text-primary-text')}
        >
          <IconMenu aria-hidden="true" className="size-5 shrink-0" />
          <span className="max-w-full truncate">{moreLabel}</span>
        </button>
      </DialogPrimitive.Trigger>
    )
  }

  // Rises from the foot like the pill it extends, above the veil and over the pill itself.
  function morePanel() {
    return (
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={cn(overlayVeil, 'bg-volee-dark/60')} />
        <DialogPrimitive.Content
          data-surface="ink"
          aria-describedby={undefined}
          className="fixed inset-x-0 bottom-0 z-(--z-dialog) flex flex-col gap-4 rounded-t-lg bg-background p-4 pr-[calc(1rem+var(--spacing-safe-right))] pb-[calc(1rem+var(--spacing-safe-bottom))] pl-[calc(1rem+var(--spacing-safe-left))] text-foreground shadow-overlay data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom"
        >
          <div className="flex items-center justify-between">
            <DialogPrimitive.Title className="text-sm font-medium">
              {moreLabel}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" aria-label={closeLabel}>
                <IconClose aria-hidden="true" />
              </Button>
            </DialogPrimitive.Close>
          </div>
          <nav
            aria-label={moreLabel}
            className="flex flex-col gap-1 overflow-y-auto"
            onClick={closeOnFollowedLink(() => setMoreOpen(false))}
          >
            <BarSlotContext.Provider value="sheet">{overflow}</BarSlotContext.Provider>
          </nav>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    )
  }

  function contentOf(slot: number): React.ReactNode {
    if (slot === HOME_SLOT) {
      return <BarSlotContext.Provider value="home">{home}</BarSlotContext.Provider>
    }
    if (needsMore && slot === MORE_SLOT) return moreButton()
    const index = places.indexOf(slot)
    return index === -1 ? null : shown[index]
  }

  // In reading order, so `Tab` and a screen reader walk the pill left to right.
  const slots = COLUMN_START.map((columnStart, index) => {
    const content = contentOf(index + 1)
    return content == null ? (
      <span key={columnStart} aria-hidden="true" className={columnStart} />
    ) : (
      <div key={columnStart} className={cn('flex min-w-0 justify-center', columnStart)}>
        {content}
      </div>
    )
  })

  return (
    <DialogPrimitive.Root open={moreOpen} onOpenChange={setMoreOpen}>
      <nav
        aria-label={label}
        data-surface="ink"
        className="fixed inset-x-3 bottom-3 z-(--z-header) mr-safe-right mb-safe-bottom ml-safe-left grid grid-cols-5 items-center rounded-pill bg-background px-2 py-1.5 shadow-overlay"
      >
        {slots}
      </nav>
      {needsMore ? morePanel() : null}
    </DialogPrimitive.Root>
  )
}
